import { Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { UserSettingsService } from './user-settings.service';
import { AttributePresetStore } from './attribute-preset-store.base';
import {
  coerceTheme,
  resolveTheme,
  ResolvedTheme,
  ThemePreset,
  THEME_KEY,
  THEME_STORAGE_KEY,
} from './theme.util';

/**
 * App-wide cache of the user's theme preference, and the only writer of the `data-theme`
 * attribute on <html>.
 *
 * Everything about HOW a preset becomes a stamped attribute and a boot mirror — the deference to
 * the index.html boot stamp, the captured-at-construction `bootStamped`, adopt()'s
 * promote-an-equal-default branch, setPreset()'s persist-before-emit ordering, the live media
 * query and its zone re-entry — lives in {@link AttributePresetStore}, extracted verbatim from
 * this class when SP3's contrast preset needed the same runtime. Each of those parts is a shipped
 * regression; the comments travelled with them.
 *
 * What is left here is the theme's own vocabulary: the four config strings, the coercion, the
 * resolution rule, and thin aliases (`theme$` / `currentTheme` / `setTheme`) so no caller had to
 * change when the shape moved.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService extends AttributePresetStore<ThemePreset, ResolvedTheme> {
  /**
   * The theme-flavoured names, kept so no caller changed when the shape moved. They are ALIASES,
   * not the only door: `preset$`, `currentPreset`, `resolved$` and `setPreset` are inherited
   * public members and a caller can reach the same behaviour through them. That is accepted
   * rather than hidden — the generic names do exactly the same thing (setTheme IS setPreset), and
   * narrowing them would mean re-declaring the base's API as protected for one subclass. Prefer
   * the theme-flavoured names in feature code; both are correct.
   */
  readonly theme$: Observable<ThemePreset> = this.preset$;

  get currentTheme(): ThemePreset {
    return this.currentPreset;
  }

  constructor(
    userSettings: UserSettingsService,
    store: Store,
    zone: NgZone,
    @Inject(DOCUMENT) doc: Document,
  ) {
    super(userSettings, store, zone, doc, {
      settingsKey: THEME_KEY,
      attributeName: 'data-theme',
      mirrorKey: THEME_STORAGE_KEY,
      mirrorArea: 'local',
      mediaQuery: '(prefers-color-scheme: light)',
    }, 'system');
  }

  setTheme(value: ThemePreset): void {
    this.setPreset(value);
  }

  protected coerce(v: unknown): ThemePreset {
    return coerceTheme(v);
  }

  /** Never null: the theme attribute is always present, one of the two known themes. So the
   *  base's PINNED_OFF_MIRROR branch and its removeAttribute branch are unreachable from here. */
  protected resolve(preset: ThemePreset, prefersLight: boolean): ResolvedTheme {
    return resolveTheme(preset, prefersLight);
  }

  /** The boot script's whitelist, restated: it stamps the mirror verbatim only for these two
   *  values and otherwise falls through to prefers-color-scheme. index.boot-theme.spec.ts pins
   *  the script's half; keep the two in step. 'off' is not among them — the theme's resolve()
   *  never returns null, so it never writes the sentinel, and a tampered one is not honoured. */
  protected resolvedFromMirror(mirror: string): ResolvedTheme | undefined {
    return mirror === 'dark' || mirror === 'light' ? mirror : undefined;
  }
}
